import React, { ReactNode, useCallback, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { withTypography } from 'ucl/components/text/Typography';

import ActionButton, { ActionLink } from 'components/action-button';
import Modal, { ModalContent } from 'components/modal';
import OrderIssues from 'pages/order-confirmation/order-issues';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { getDesignToken } from 'styles/constants';
import { routes } from 'utils/routing';

import HeadingContainer from './cart-error-heading-wrapper';

const ButtonGutter = Box.withConfig({
  flexDirection: { base: 'column', md: 'row' },
  justifyContent: { base: 'center', md: 'space-between' },
  marginTop: '$8',
});

const Body = withTypography({
  mx: '$0',
  textAlign: 'justify',
  fontSize: 'md',
  style: { marginTop: 8 },
});

const Disclaimer = withTypography({
  variant: 'copyTwo',
  textAlign: 'center',
  mt: '$8',
  style: { maxWidth: 256 },
});

const LinkWrapper = Box.withConfig({
  textAlign: 'center',
});

interface ICartError {
  title?: string;
  body?: ReactNode;
  disclaimer?: string;
  onAccept?: () => void;
  onDismiss?: () => void;
  additionalLoggingContext: Object;
  actionButtonText?: string;
  onActionButtonPress?: () => void;
}

const message = 'Cart Error: Order could not be completed';

const CartError: React.FC<React.PropsWithChildren<ICartError>> = ({
  title,
  body,
  disclaimer,
  onAccept,
  onDismiss,
  additionalLoggingContext,
  actionButtonText,
  onActionButtonPress,
}) => {
  const isShowingOrderingIssue = useFlag(LaunchDarklyFlag.SHOW_ORDERING_ISSUE);
  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const router = useRouter();

  const startOverCallback = useCallback(() => {
    router.replace({ pathname: routes.storeLocator, params: { redirectUrl: routes.cart } });
    shouldConfirmStoreOnCheckout(true);
  }, [shouldConfirmStoreOnCheckout, router]);

  const modalHeading = title || t('restaurantUnavailableErrorTitle');
  const modalBody = body || t('restaurantUnavailableErrorMessage');

  return (
    <Modal
      additionalLoggingContext={useMemo(
        () => ({
          ...additionalLoggingContext,
          guestMessageTitle: title,
          isOrderCouldNotBeCompletedError: true,
        }),
        [additionalLoggingContext, title]
      )}
      onDismiss={onDismiss}
      isErrorModal
      amplitudeEventData={{
        modalAppearanceEventMessage: message,
        modalMessage: typeof modalBody === 'string' ? modalBody : undefined,
      }}
      backgroundColor={getDesignToken('background-default')}
      footer={isShowingOrderingIssue && <OrderIssues />}
    >
      <ModalContent alignItems="center" paddingX="$8" testID="cart-error-modal-content">
        <HeadingContainer modalHeading={modalHeading} />
        <Body>{modalBody}</Body>
        {disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
        <ButtonGutter>
          {onAccept && (
            <LinkWrapper>
              <ActionLink onPress={onAccept} to={'.'} fullWidth>
                {t('accept')}
              </ActionLink>
            </LinkWrapper>
          )}
          <LinkWrapper>
            <ActionButton
              onPress={onActionButtonPress || startOverCallback}
              testID="cart-error-start-over"
              fullWidth
            >
              {actionButtonText || t('restartPurchaseProcessButton')}
            </ActionButton>
          </LinkWrapper>
        </ButtonGutter>
      </ModalContent>
    </Modal>
  );
};

export default CartError;
